<template>
	<div id="directory-detail-page" data-aos="fade">
		<!-- No encontrado -->
		<section v-if="!profesional" class="content-section">
			<div class="container text-center py-5">
				<h1 class="not-found-title">Profesional no encontrado</h1>
				<b-button to="/directorio" variant="info">
					<i class="fa-regular fa-arrow-left mr-2"></i>
					Volver al directorio
				</b-button>
			</div>
		</section>

		<section v-else class="content-section">
			<div class="container">
				<div class="d-flex flex-wrap align-items-center justify-content-between mb-4 toolbar" data-aos="fade-down">
					<b-button to="/directorio" variant="link" class="back-link pl-0">
						<i class="fa-regular fa-arrow-left mr-2"></i>
						Volver al directorio
					</b-button>
					<b-button variant="outline-secondary" @click="handleShare">
						<i class="fa-regular fa-share-nodes mr-2"></i>
						Compartir perfil
					</b-button>
				</div>

				<div class="row">
					<!-- Columna principal -->
					<div class="col-lg-8 mb-4 mb-lg-0">
						<!-- Perfil -->
						<div class="detail-card mb-4" data-aos="fade-up">
							<div class="d-flex flex-column flex-md-row profile-header">
								<div class="avatar-wrap mb-3 mb-md-0 mr-md-4">
									<img
										v-if="profesional.avatar"
										:src="profesional.avatar"
										:alt="profesional.nombre"
										class="avatar-img"
									/>
									<div v-else class="avatar-fallback">
										{{ initials }}
									</div>
								</div>

								<div class="flex-grow-1">
									<div class="d-flex align-items-center flex-wrap mb-2">
										<h1 class="profile-name mb-0 mr-2">
											{{ fullName }}
										</h1>
										<i
											v-if="profesional.verificado"
											class="fa-solid fa-circle-check verified-icon"
											title="Verificado"
										></i>
									</div>

									<span class="tipo-badge mb-3">
										{{ profesional.tipo === 'abogado' ? 'Abogado Particular' : 'Firma Legal' }}
									</span>

									<div class="meta-row mb-3">
										<span v-if="profesional.ubicacion" class="meta-item">
											<i class="fa-regular fa-location-dot"></i>
											{{ profesional.ubicacion }}
										</span>
										<span v-if="profesional.experiencia" class="meta-item">
											<i class="fa-regular fa-calendar"></i>
											{{ profesional.experiencia }} de experiencia
										</span>
										<span v-if="profesional.rating" class="meta-item">
											<i class="fa-solid fa-star star-icon"></i>
											{{ profesional.rating }} ({{ profesional.reviews }} reseñas)
										</span>
									</div>

									<span class="especialidad-badge">
										{{ especialidadLabel }}
									</span>

									<div
										v-if="profesional.redesSociales"
										class="social-links mt-3"
									>
										<a
											v-if="profesional.redesSociales.website"
											:href="profesional.redesSociales.website"
											target="_blank"
											rel="noopener noreferrer"
											class="social-link"
										>
											<i class="fa-regular fa-globe"></i>
											Sitio web
										</a>
										<a
											v-if="profesional.redesSociales.linkedin"
											:href="profesional.redesSociales.linkedin"
											target="_blank"
											rel="noopener noreferrer"
											class="social-link"
										>
											<i class="fa-brands fa-linkedin"></i>
											LinkedIn
										</a>
										<a
											v-if="profesional.redesSociales.facebook"
											:href="profesional.redesSociales.facebook"
											target="_blank"
											rel="noopener noreferrer"
											class="social-link"
										>
											<i class="fa-brands fa-facebook"></i>
											Facebook
										</a>
										<a
											v-if="profesional.redesSociales.tiktok"
											:href="profesional.redesSociales.tiktok"
											target="_blank"
											rel="noopener noreferrer"
											class="social-link"
										>
											<i class="fa-brands fa-tiktok"></i>
											TikTok
										</a>
										<a
											v-if="profesional.redesSociales.youtube"
											:href="profesional.redesSociales.youtube"
											target="_blank"
											rel="noopener noreferrer"
											class="social-link"
										>
											<i class="fa-brands fa-youtube"></i>
											YouTube
										</a>
									</div>
								</div>
							</div>
						</div>

						<!-- Acerca de -->
						<div class="detail-card mb-4" data-aos="fade-up" data-aos-delay="50">
							<h2 class="card-heading">Acerca de</h2>
							<p class="about-text">{{ profesional.descripcion }}</p>
							<p class="about-text mb-0">{{ aboutExtra }}</p>
						</div>

						<!-- Educación -->
						<div
							v-if="profesional.tipo === 'abogado' && profesional.educacion"
							class="detail-card mb-4"
							data-aos="fade-up"
						>
							<h2 class="card-heading">
								<i class="fa-regular fa-award mr-2"></i>
								Educación
							</h2>
							<ul class="edu-list list-unstyled mb-0">
								<li
									v-for="(edu, index) in profesional.educacion"
									:key="index"
									class="edu-item"
								>
									<i class="fa-regular fa-circle-check"></i>
									<span>{{ edu }}</span>
								</li>
							</ul>
						</div>

						<!-- Idiomas -->
						<div v-if="profesional.idiomas" class="detail-card" data-aos="fade-up">
							<h2 class="card-heading">Idiomas</h2>
							<div class="idioma-list">
								<span
									v-for="(idioma, index) in profesional.idiomas"
									:key="index"
									class="idioma-badge"
								>
									{{ idioma }}
								</span>
							</div>
						</div>
					</div>

					<!-- Sidebar -->
					<div class="col-lg-4">
						<!-- Contacto -->
						<div class="detail-card mb-4" data-aos="fade-up" data-aos-delay="50">
							<h2 class="card-heading">Contacto</h2>
							<div class="contact-list mb-3">
								<a :href="'tel:' + profesional.contacto.telefono" class="contact-item">
									<i class="fa-regular fa-phone"></i>
									<span>{{ profesional.contacto.telefono }}</span>
								</a>
								<a :href="'mailto:' + profesional.contacto.email" class="contact-item">
									<i class="fa-regular fa-envelope"></i>
									<span>{{ profesional.contacto.email }}</span>
								</a>
							</div>

							<b-button
								variant="info"
								block
								size="lg"
								:disabled="!profesional.disponible"
							>
								<i class="fa-regular fa-comments mr-2"></i>
								{{ profesional.disponible ? 'Contactar ahora' : 'No disponible' }}
							</b-button>
							<p v-if="!profesional.disponible" class="unavailable-note mb-0 mt-2">
								Este profesional no está disponible en este momento
							</p>
						</div>

						<!-- Costos -->
						<div class="detail-card mb-4" data-aos="fade-up" data-aos-delay="100">
							<h2 class="card-heading">
								<i class="fa-regular fa-dollar-sign mr-2"></i>
								Costo por consulta/hr
							</h2>
							<div class="price-row">
								<span>Consulta inicial</span>
								<strong>$1,500 MXN</strong>
							</div>
							<div class="price-row">
								<span>Tarifa por hora</span>
								<strong>$2,500 MXN</strong>
							</div>
							<p class="price-note mb-0">
								* Los precios pueden variar según la complejidad del caso
							</p>
						</div>

						<!-- Estadísticas -->
						<div v-if="profesional.casos" class="detail-card" data-aos="fade-up" data-aos-delay="200">
							<h2 class="card-heading">Estadísticas</h2>
							<div class="stat-row">
								<span>Casos atendidos</span>
								<strong>{{ profesional.casos }}+</strong>
							</div>
							<div v-if="profesional.rating" class="stat-row">
								<span>Calificación</span>
								<strong class="d-inline-flex align-items-center">
									<i class="fa-solid fa-star star-icon mr-1"></i>
									{{ profesional.rating }}
								</strong>
							</div>
							<div v-if="profesional.reviews" class="stat-row mb-0">
								<span>Reseñas</span>
								<button
									type="button"
									class="reviews-trigger"
									@click="showReviewsModal = true"
								>
									{{ profesional.reviews }}
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>

		<!-- Modal reseñas -->
		<b-modal
			v-model="showReviewsModal"
			title="Reseñas de clientes"
			size="lg"
			hide-footer
			scrollable
			modal-class="directory-modal"
		>
			<p v-if="profesional" class="reviews-summary">
				{{ profesional.reviews }} reseñas • Calificación promedio: {{ profesional.rating }}/5.0
			</p>

			<template v-if="profesional && profesional.resenas && profesional.resenas.length">
				<div
					v-for="resena in profesional.resenas"
					:key="resena.id"
					class="review-card"
				>
					<div class="d-flex justify-content-between align-items-start mb-2">
						<div>
							<p class="review-author mb-0">{{ resena.autor }}</p>
							<p class="review-date mb-0">{{ resena.fecha }}</p>
						</div>
						<div class="review-stars">
							<i
								v-for="n in 5"
								:key="n"
								class="fa-solid fa-star"
								:class="n <= resena.rating ? 'star-icon' : 'star-empty'"
							></i>
						</div>
					</div>
					<p class="review-comment mb-0">{{ resena.comentario }}</p>
				</div>
			</template>
			<p v-else class="text-center text-muted py-4 mb-0">
				No hay reseñas disponibles aún.
			</p>
		</b-modal>

		<!-- Modal compartir -->
		<b-modal
			v-model="showShareModal"
			title="Compartir perfil"
			hide-footer
			modal-class="directory-modal"
		>
			<p class="share-desc">Comparte este perfil con otros</p>

			<div class="d-flex align-items-center mb-3">
				<b-form-input :value="shareUrl" readonly class="mr-2"></b-form-input>
				<b-button variant="outline-secondary" size="sm" @click="copyToClipboard">
					<i v-if="copied" class="fa-regular fa-check"></i>
					<span v-else>Copiar</span>
				</b-button>
			</div>

			<p class="share-label">Compartir en redes sociales:</p>
			<div class="d-flex mb-3">
				<b-button
					variant="outline-secondary"
					size="sm"
					class="flex-fill mr-2"
					@click="shareFacebook"
				>
					<i class="fa-brands fa-facebook mr-1"></i>
					Facebook
				</b-button>
				<b-button
					variant="outline-secondary"
					size="sm"
					class="flex-fill"
					@click="shareLinkedIn"
				>
					<i class="fa-brands fa-linkedin mr-1"></i>
					LinkedIn
				</b-button>
			</div>

			<b-button variant="outline-secondary" block @click="shareWhatsApp">
				<i class="fa-brands fa-whatsapp mr-2"></i>
				Compartir por WhatsApp
			</b-button>
		</b-modal>
	</div>
</template>

<script>
const ESPECIALIDADES = {
	civil: 'Derecho Civil',
	penal: 'Derecho Penal',
	laboral: 'Derecho Laboral',
	familiar: 'Derecho Familiar',
	mercantil: 'Derecho Mercantil',
	fiscal: 'Derecho Fiscal',
	inmobiliario: 'Derecho Inmobiliario',
	seguros: 'Derecho de Seguros',
};

const PROFESIONALES = [
	{
		id: '1',
		tipo: 'abogado',
		nombre: 'María Elena',
		apellidos: 'González Ruiz',
		especialidad: 'civil',
		disponible: true,
		verificado: true,
		descripcion:
			'Especialista en derecho civil con amplia experiencia en contratos, responsabilidad civil y derecho inmobiliario. Comprometida con la defensa de los derechos de mis clientes y la búsqueda de soluciones efectivas.',
		avatar: null,
		contacto: {
			telefono: '+52 55 1234 5678',
			email: 'maria.gonzalez@email.com',
		},
		ubicacion: 'Ciudad de México',
		experiencia: '15 años',
		rating: 4.9,
		reviews: 127,
		casos: 350,
		educacion: [
			'Licenciatura en Derecho - UNAM',
			'Maestría en Derecho Civil - Universidad Panamericana',
		],
		idiomas: ['Español', 'Inglés'],
		redesSociales: {
			website: 'https://mariagonzalez.com',
			linkedin: 'https://linkedin.com/in/mariagonzalez',
		},
		resenas: [
			{
				id: '1',
				autor: 'Juan Pérez',
				rating: 5,
				fecha: '15 de marzo, 2025',
				comentario: 'Excelente abogada, muy profesional y dedicada. Resolvió mi caso de manera eficiente.',
			},
			{
				id: '2',
				autor: 'Laura Martínez',
				rating: 5,
				fecha: '10 de febrero, 2025',
				comentario: 'Muy recomendable. Su experiencia y conocimiento fueron clave para ganar mi caso.',
			},
			{
				id: '3',
				autor: 'Roberto Sánchez',
				rating: 4,
				fecha: '5 de enero, 2025',
				comentario: 'Buena atención y seguimiento constante. Muy satisfecho con el resultado.',
			},
		],
	},
	{
		id: '2',
		tipo: 'firma',
		nombre: 'Martínez & Asociados',
		especialidad: 'laboral',
		disponible: true,
		verificado: true,
		descripcion:
			'Firma especializada en derecho laboral con más de 20 años de experiencia. Contamos con un equipo de 15 abogados especializados en todas las áreas del derecho del trabajo.',
		avatar: null,
		contacto: {
			telefono: '+52 55 1234 5678',
			email: 'contacto@martinezasociados.com',
		},
		ubicacion: 'Monterrey, Nuevo León',
		experiencia: '20 años',
		rating: 4.8,
		reviews: 245,
		casos: 1200,
		idiomas: ['Español', 'Inglés', 'Francés'],
		redesSociales: {
			website: 'https://martinezasociados.com',
			linkedin: 'https://linkedin.com/company/martinezasociados',
			facebook: 'https://facebook.com/martinezasociados',
			tiktok: 'https://tiktok.com/@martinezasociados',
			youtube: 'https://youtube.com/@martinezasociados',
		},
		resenas: [
			{
				id: '1',
				autor: 'Empresa ABC S.A.',
				rating: 5,
				fecha: '20 de marzo, 2025',
				comentario: 'Excelente firma. Nos han asesorado en múltiples casos laborales con resultados excepcionales.',
			},
			{
				id: '2',
				autor: 'Pedro Ramírez',
				rating: 5,
				fecha: '18 de marzo, 2025',
				comentario: 'Profesionales de primer nivel. Ganamos el caso de despido injustificado gracias a su trabajo.',
			},
			{
				id: '3',
				autor: 'Ana López',
				rating: 4,
				fecha: '12 de marzo, 2025',
				comentario: 'Muy buena atención y conocimiento profundo del derecho laboral.',
			},
			{
				id: '4',
				autor: 'Carlos Mendoza',
				rating: 5,
				fecha: '8 de marzo, 2025',
				comentario: 'Recomendados al 100%. Su equipo es muy profesional y eficiente.',
			},
			{
				id: '5',
				autor: 'Sofía García',
				rating: 5,
				fecha: '1 de marzo, 2025',
				comentario: 'Excelente servicio. Resolvieron mi caso laboral de manera rápida y efectiva.',
			},
		],
	},
	{
		id: '3',
		tipo: 'abogado',
		nombre: 'Carlos Alberto',
		apellidos: 'Mendoza López',
		especialidad: 'laboral',
		disponible: true,
		verificado: true,
		descripcion:
			'Abogado laboralista con experiencia en despidos injustificados, demandas laborales y negociación colectiva. Mi enfoque es lograr acuerdos justos para mis clientes.',
		avatar: null,
		contacto: {
			telefono: '+52 55 9876 5432',
			email: 'carlos.mendoza@email.com',
		},
		ubicacion: 'Guadalajara, Jalisco',
		experiencia: '12 años',
		rating: 4.7,
		reviews: 89,
		casos: 280,
		educacion: [
			'Licenciatura en Derecho - Universidad de Guadalajara',
			'Especialidad en Derecho Laboral - ITESO',
		],
		idiomas: ['Español'],
	},
	{
		id: '4',
		tipo: 'firma',
		nombre: 'López Legal Group',
		especialidad: 'fiscal',
		disponible: true,
		verificado: true,
		descripcion:
			'Grupo legal especializado en derecho fiscal y tributario. Asesoramos a empresas y particulares en planeación fiscal, auditorías y controversias fiscales.',
		avatar: null,
		contacto: {
			telefono: '+52 55 9876 5432',
			email: 'info@lopezlegal.com',
		},
		ubicacion: 'Ciudad de México',
		experiencia: '18 años',
		rating: 4.9,
		reviews: 312,
		casos: 950,
		idiomas: ['Español', 'Inglés'],
	},
	{
		id: '5',
		tipo: 'abogado',
		nombre: 'Ana Patricia',
		apellidos: 'Herrera Vega',
		especialidad: 'familiar',
		disponible: false,
		verificado: true,
		descripcion:
			'Especialista en derecho familiar con enfoque en divorcios, custodia de menores y pensiones alimenticias. Manejo cada caso con sensibilidad y profesionalismo.',
		avatar: null,
		contacto: {
			telefono: '+52 55 5555 1234',
			email: 'ana.herrera@email.com',
		},
		ubicacion: 'Puebla, Puebla',
		experiencia: '10 años',
		rating: 4.8,
		reviews: 156,
		casos: 220,
		educacion: [
			'Licenciatura en Derecho - BUAP',
			'Diplomado en Derecho Familiar - Universidad Iberoamericana',
		],
		idiomas: ['Español', 'Inglés'],
	},
	{
		id: '6',
		tipo: 'firma',
		nombre: 'García & Partners',
		especialidad: 'mercantil',
		disponible: true,
		verificado: true,
		descripcion:
			'Firma de abogados especializada en derecho mercantil y corporativo. Asesoramos en fusiones, adquisiciones, contratos comerciales y gobierno corporativo.',
		avatar: null,
		contacto: {
			telefono: '+52 55 5555 1234',
			email: 'contacto@garciapartners.com',
		},
		ubicacion: 'Ciudad de México',
		experiencia: '25 años',
		rating: 4.9,
		reviews: 428,
		casos: 1500,
		idiomas: ['Español', 'Inglés', 'Alemán'],
	},
];

export default {
	name: 'DirectoryDetailPage',
	data() {
		return {
			showReviewsModal: false,
			showShareModal: false,
			copied: false,
			copyTimeout: null,
			profesionalesData: PROFESIONALES,
		};
	},
	computed: {
		profesional() {
			return this.profesionalesData.find((p) => p.id === this.$route.params.id) || null;
		},
		fullName() {
			if (!this.profesional) return '';
			return this.profesional.apellidos
				? this.profesional.nombre + ' ' + this.profesional.apellidos
				: this.profesional.nombre;
		},
		initials() {
			if (!this.profesional) return '';
			const a = this.profesional.nombre.charAt(0);
			const b = this.profesional.apellidos ? this.profesional.apellidos.charAt(0) : '';
			return (a + b).toUpperCase();
		},
		especialidadLabel() {
			if (!this.profesional) return '';
			return ESPECIALIDADES[this.profesional.especialidad] || this.profesional.especialidad;
		},
		aboutExtra() {
			if (!this.profesional) return '';
			if (this.profesional.tipo === 'abogado') {
				return 'Con una sólida trayectoria profesional, me dedico a brindar asesoría legal personalizada y de alta calidad. Mi enfoque se centra en entender las necesidades específicas de cada cliente para ofrecer soluciones efectivas y estratégicas. Cuento con experiencia comprobada en casos complejos y mantengo un compromiso constante con la actualización en las últimas reformas y jurisprudencias relevantes.';
			}
			return 'Nuestra firma se distingue por ofrecer servicios legales integrales con un equipo multidisciplinario de profesionales altamente capacitados. Nos especializamos en brindar soluciones innovadoras y estratégicas, adaptadas a las necesidades específicas de cada cliente. Contamos con una amplia red de contactos en el sector legal y mantenemos alianzas estratégicas con expertos en diversas áreas del derecho para garantizar el mejor resultado en cada caso.';
		},
		shareUrl() {
			return typeof window !== 'undefined' ? window.location.href : '';
		},
		shareText() {
			if (!this.profesional) return '';
			const tipo = this.profesional.tipo === 'abogado' ? 'Abogado' : 'Firma';
			return (
				'Mira el perfil de ' +
				this.fullName +
				' - ' +
				tipo +
				' especializado en ' +
				this.especialidadLabel
			);
		},
	},
	watch: {
		profesional: {
			immediate: true,
			handler(val) {
				if (val) {
					document.title = this.fullName + ' - Unlawk';
				}
			},
		},
	},
	beforeDestroy() {
		if (this.copyTimeout) {
			clearTimeout(this.copyTimeout);
		}
	},
	methods: {
		async handleShare() {
			if (navigator.share) {
				try {
					await navigator.share({
						title: this.fullName + ' - unlawk',
						text: this.shareText,
						url: this.shareUrl,
					});
				} catch (err) {
					// cancelado por el usuario
				}
			} else {
				this.showShareModal = true;
			}
		},
		copyToClipboard() {
			if (navigator.clipboard && navigator.clipboard.writeText) {
				navigator.clipboard.writeText(this.shareUrl);
			} else {
				const input = document.createElement('input');
				input.value = this.shareUrl;
				document.body.appendChild(input);
				input.select();
				document.execCommand('copy');
				document.body.removeChild(input);
			}
			this.copied = true;
			if (this.copyTimeout) clearTimeout(this.copyTimeout);
			this.copyTimeout = setTimeout(() => {
				this.copied = false;
			}, 2000);
		},
		shareFacebook() {
			const url = encodeURIComponent(this.shareUrl);
			window.open('https://www.facebook.com/sharer/sharer.php?u=' + url, '_blank');
		},
		shareLinkedIn() {
			const url = encodeURIComponent(this.shareUrl);
			window.open('https://www.linkedin.com/sharing/share-offsite/?url=' + url, '_blank');
		},
		shareWhatsApp() {
			const url = encodeURIComponent(this.shareUrl);
			const text = encodeURIComponent('Mira el perfil de ' + this.fullName + ' en unlawk: ');
			window.open('https://wa.me/?text=' + text + url, '_blank');
		},
	},
};
</script>
